/**
 * Workspace feature-flag context (customization US-007).
 *
 * Fetches /api/workspaces/<active>/features whenever the active workspace
 * changes. Exposes:
 *   - the typed flag map (`features`)
 *   - `isFeatureEnabled(key)`: returns true unless the flag has been
 *     EXPLICITLY set to false. `undefined` means platform default → on.
 *   - `refresh()` to re-pull after the settings UI saves.
 *
 * The contract matches the backend's enforcement model in
 * `featuresGate(featureKey)`: a missing flag = enabled.
 *
 * Falls back silently to "all features enabled" when:
 *   - no active workspace yet (loading)
 *   - the /features endpoint 404s or errors
 *   - SSO isn't wired in (single-tenant mode)
 *
 * Used by:
 *   - <Sidebar> to filter NAV_GROUPS by `feature` key
 *   - <FeatureGate feature="..."> to wrap routes / render-time gates
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api';
import { useActiveWorkspace } from './ActiveWorkspaceContext';
import type { WorkspaceFeatureFlags } from '../types';

export type FeatureKey = keyof WorkspaceFeatureFlags;

interface FeaturesContextValue {
  features: WorkspaceFeatureFlags;
  isFeatureEnabled: (key: FeatureKey) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<FeaturesContextValue>({
  features: {},
  isFeatureEnabled: () => true,
  refresh: () => Promise.resolve(),
});

/** Pure helper — exported so non-React code (tests, route-guards) can
 *  reuse the same semantics. `undefined` → true (default-on). */
export function isFeatureEnabledIn(
  features: WorkspaceFeatureFlags | null | undefined,
  key: FeatureKey,
): boolean {
  if (!features) return true;
  return features[key] !== false;
}

export function WorkspaceFeaturesProvider({ children }: { children: ReactNode }) {
  const { active } = useActiveWorkspace();
  const [features, setFeatures] = useState<WorkspaceFeatureFlags>({});

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const f = await api.getWorkspaceFeatures(active.id);
      setFeatures(f ?? {});
    } catch {
      // Treat any error as "all features enabled" so an incident with
      // the features endpoint doesn't blank out the whole nav.
      setFeatures({});
    }
  }, [active]);

  useEffect(() => {
    void load();
  }, [load]);

  const isFeatureEnabled = useCallback(
    (key: FeatureKey) => isFeatureEnabledIn(features, key),
    [features],
  );

  const value = useMemo<FeaturesContextValue>(
    () => ({ features, isFeatureEnabled, refresh: load }),
    [features, isFeatureEnabled, load],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFeatures(): FeaturesContextValue {
  return useContext(Ctx);
}
